/**
 * Free and Liquidity+: what each one includes, and which one an account is on.
 *
 * Shared by the server, which enforces the limits, and by the client, which
 * explains them -- so the two can never disagree about what a limit is. The
 * prices live here for the same reason: the app, the landing page and the
 * checkout all quote them, and a figure written down in three places is a
 * figure that will one day be wrong in one of them.
 *
 * Nothing here reads a clock or a database. The caller passes both in, which
 * is what lets the tests ask "and on the day after launch?" directly.
 */

export const PLAN_FREE = 'free';
export const PLAN_PLUS = 'plus';

/**
 * What each plan allows. `null` means no limit.
 *
 * Free for your money, paid for your trading and automation (October 2026,
 * ahead of Product Hunt): the whole ledger is free, statement imports
 * included; Liquidity+ is for active traders and for not typing prices.
 *
 * - csvImports: statements imported, counted from the launch, for the life of
 *   the account. No limit on either plan since October 2026 -- importing is
 *   how people get their data in, and three imports sent them back to typing.
 *   Kept as a limit so the machinery stays should it ever return.
 * - journalPerMonth: trades added to the journal in one calendar month.
 * - backtestSessions / propAccounts: sessions and prop-firm accounts an
 *   account can have at once; what is already there stays, only adding more
 *   is refused.
 * - taxes: the tax summaries -- PIT-38, US Forms 6781 and 8949, and whatever country comes next.
 * - quotes: automatic prices for holdings (a price typed by hand is always free).
 * - serverAi: category suggestions on this service's own model key.
 */
export const PLAN_LIMITS = Object.freeze({
  free: Object.freeze({
    csvImports: null, journalPerMonth: 20, backtestSessions: 1, propAccounts: 1,
    taxes: false, quotes: false, serverAi: false
  }),
  plus: Object.freeze({
    csvImports: null, journalPerMonth: null, backtestSessions: null, propAccounts: null,
    taxes: true, quotes: true, serverAi: true
  })
});

/*
 * The day the free plan's new limits -- backtesting, prop firms, the tax summaries --
 * begin: the day version 5 of the terms, which describes them, takes effect
 * (TERMS_UPCOMING in model.js; a test holds the two to the same day). Until
 * then the free plan keeps those as it had them, with no limit. Unlimited
 * imports needed no notice and apply already.
 */
export const PLAN_CHANGES_FROM = '2026-10-25T00:00:00+02:00';

const FREE_BEFORE_CHANGES = Object.freeze({
  ...PLAN_LIMITS.free, backtestSessions: null, propAccounts: null, taxes: true
});

/** The free plan's limits at a moment: the new ones from PLAN_CHANGES_FROM on. */
export function freeLimitsAt(now = new Date(), changesFrom = PLAN_CHANGES_FROM) {
  return changesFrom && time(now) < time(changesFrom) ? FREE_BEFORE_CHANGES : PLAN_LIMITS.free;
}

/*
 * Prices by region -- what a month of Liquidity+ is worth where somebody lives.
 *
 * Three regions, decided with the operator in October 2026:
 *
 * - eu: the European Union and the EEA, at the Polish price. EU law on
 *   geo-blocking (2018/302) forbids refusing a customer from one member state
 *   the terms given to another, so the whole EEA shares one price level --
 *   shown in złoty in Poland and in euro elsewhere;
 * - global: high-income countries outside it (the US, the UK, Switzerland,
 *   Canada, Australia...), where 10 zł would be a fifth of what comparable
 *   tools ask;
 * - emerging: everywhere else, at a price set by purchasing power.
 *
 * Gross amounts, in the currency's own units. Yearly is about eight months'
 * price. A visitor's region is a guess from the browser's time zone, open to
 * correction; the card's country is the check -- see alignRegion on the server.
 */
export const REGIONS = Object.freeze(['eu', 'global', 'emerging']);

export const PRICES = Object.freeze({
  eu: Object.freeze({
    PLN: Object.freeze({ month: 10, year: 80 }),
    EUR: Object.freeze({ month: 2.99, year: 24 })
  }),
  global: Object.freeze({
    USD: Object.freeze({ month: 5.99, year: 49 }),
    GBP: Object.freeze({ month: 4.99, year: 39 }),
    CHF: Object.freeze({ month: 5.9, year: 49 })
  }),
  emerging: Object.freeze({
    USD: Object.freeze({ month: 1.99, year: 15 })
  })
});

/** The European Union and the rest of the EEA (Iceland, Liechtenstein, Norway). */
export const EU_COUNTRIES = Object.freeze([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU',
  'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'IS', 'LI', 'NO',
  /* Territories that are part of the EU, and the micro-states that use the euro with it. */
  'GF', 'GP', 'MQ', 'RE', 'YT', 'MF', 'AX', 'MC', 'SM', 'VA', 'AD'
]);

/** High-income countries and territories outside the EEA (World Bank classification). */
export const GLOBAL_COUNTRIES = Object.freeze([
  'US', 'CA', 'GB', 'CH', 'AU', 'NZ', 'JP', 'KR', 'SG', 'HK', 'MO', 'TW', 'IL', 'AE', 'QA', 'KW', 'BH', 'SA',
  'OM', 'BN', 'IM', 'JE', 'GG', 'GI', 'FO', 'GL', 'BM', 'KY', 'BS', 'AW', 'CW', 'SX', 'PR', 'GU', 'VI', 'MP',
  'AS', 'PF', 'NC', 'TC', 'VG', 'UY', 'PA', 'BB', 'KN', 'AG', 'TT', 'SC', 'NR', 'PM', 'BL'
]);

/** The region a country prices in. An unknown country is priced as `global`. */
export function regionOfCountry(country) {
  const code = String(country || '').toUpperCase();
  if (EU_COUNTRIES.includes(code)) return 'eu';
  if (GLOBAL_COUNTRIES.includes(code)) return 'global';
  return code.length === 2 ? 'emerging' : 'global';
}

/** The currency a region is shown in for a country: złoty at home, the local one where it is on offer. */
export function currencyFor(region, country) {
  const code = String(country || '').toUpperCase();
  if (region === 'eu') return code === 'PL' ? 'PLN' : 'EUR';
  if (region === 'global') {
    if (['GB', 'IM', 'JE', 'GG'].includes(code)) return 'GBP';
    if (code === 'CH') return 'CHF';
    return 'USD';
  }
  return 'USD';
}

/** A region and currency that exist together, or null. */
export function priceOf(region, currency, interval) {
  return PRICES[region]?.[currency]?.[interval] ?? null;
}

/** Every region-and-currency pair on sale, in the order a picker lists them. */
export const PRICE_OPTIONS = Object.freeze(
  REGIONS.flatMap(region => Object.keys(PRICES[region]).map(currency => Object.freeze({ region, currency })))
);

/**
 * The Stripe lookup key of each price -- so the app never stores a Stripe id,
 * and the setup script creates them by name. The Polish prices keep the keys
 * they were launched under.
 */
export function priceKey(region, currency, interval) {
  if (region === 'eu' && currency === 'PLN') return interval === 'year' ? 'liquidity_plus_year' : 'liquidity_plus_month';
  return `liquidity_plus_${region}_${currency.toLowerCase()}_${interval}`;
}

/** Which price a lookup key stands for: { region, currency, interval, amount, tier }, or null. */
export function priceOfKey(key) {
  for (const { region, currency } of PRICE_OPTIONS) {
    for (const interval of ['month', 'year']) {
      if (priceKey(region, currency, interval) === key) {
        /* `tier` is the region, under the name the billing view has always used. */
        return { region, currency, interval, amount: PRICES[region][currency][interval], tier: region };
      }
    }
  }
  return null;
}

/** An amount in its currency, as a person reads it: "10 zł", "€2.99", "$49". */
export function formatPrice(amount, currency, language = 'EN') {
  const whole = Number.isInteger(amount);
  return new Intl.NumberFormat(language === 'PL' ? 'pl-PL' : 'en-US', {
    style: 'currency', currency, minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2
  }).format(amount);
}

/*
 * How long Liquidity+ outlives a subscription's paid period: a renewal is
 * charged at the very end of it, and the confirmation can take a while to
 * arrive. A failed payment gets a week, while Stripe retries the card.
 */
export const RENEWAL_GRACE_DAYS = 2;
export const PAST_DUE_GRACE_DAYS = 7;

/** A new account's Liquidity+ trial, no card asked for. */
export const TRIAL_DAYS = 14;

/** Accounts that existed before the launch get this long on Liquidity+, as thanks. */
export const LAUNCH_GIFT_MONTHS = 3;

/** Descriptions one Liquidity+ account may have sorted on the service's key, per month. */
export const SERVER_AI_MONTHLY_ROWS = 1500;

/**
 * The moment the limits began (an ISO date or date-time), or null before plans.
 *
 * Every account that existed before it has Liquidity+ for three months from it,
 * so nobody lost anything on the day; accounts made after it start a trial.
 * The terms (section 6.1) name this day -- change one, change the other.
 */
export const PLANS_LAUNCH = '2026-09-28T14:30:00Z';

const DAY_MS = 24 * 60 * 60 * 1000;

const time = value => (value instanceof Date ? value.getTime() : Date.parse(value));

export function addDays(iso, days) {
  return new Date(time(iso) + days * DAY_MS).toISOString();
}

export function addMonths(iso, months) {
  const date = new Date(time(iso));
  const day = date.getUTCDate();
  date.setUTCDate(1);
  date.setUTCMonth(date.getUTCMonth() + months);
  /* 31 January plus a month is the last day of February, not 3 March. */
  const last = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate();
  date.setUTCDate(Math.min(day, last));
  return date.toISOString();
}

/** The first instant of the calendar month `now` falls in, UTC. */
export function monthStart(now = new Date()) {
  const date = new Date(time(now));
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1)).toISOString();
}

/**
 * Which plan an account is on at `now`, and what that allows.
 *
 * @param user  { plan, planUntil, planSource, createdAt, isGuest }
 * @returns { plan, source, until, enforced, launch, limits, giftUntil }
 *
 * Liquidity+ can come from several places at once -- a subscription, a trial,
 * the launch gift, a grant by hand -- and the one that lasts longest is the one
 * reported, so a trial never hides a subscription that outlives it.
 *
 * Before the launch nothing is limited, whatever the plan says: the terms that
 * describe the limits are not in force yet. That includes the suggestions on
 * the service's model key -- they replaced the keys people used to bring, and
 * nobody loses them before the day the plans begin.
 */
export function planFor(user, { now = new Date(), launch = PLANS_LAUNCH, changesFrom = PLAN_CHANGES_FROM } = {}) {
  const at = time(now);
  const start = launch ? time(launch) : null;
  const enforced = start !== null && at >= start;
  const createdBeforeLaunch = start !== null && time(user.createdAt) < start;
  const giftUntil = createdBeforeLaunch ? addMonths(launch, LAUNCH_GIFT_MONTHS) : null;

  const offers = [];
  /* Bought or granted. 'pro' is what the column said before there was a Liquidity+. */
  if (user.plan === PLAN_PLUS || user.plan === 'pro') {
    offers.push({ source: user.planSource || 'manual', until: user.planUntil || null });
  }
  /* The trial and the gift are about the limits, so they begin with them. */
  if (enforced) {
    offers.push(createdBeforeLaunch
      ? { source: 'gift', until: giftUntil }
      : { source: 'trial', until: addDays(user.createdAt, TRIAL_DAYS) });
  }

  const best = offers
    .filter(offer => offer.until === null || time(offer.until) > at)
    .sort((a, b) => (b.until === null ? Infinity : time(b.until)) - (a.until === null ? Infinity : time(a.until)))[0]
    || null;

  let limits = best ? PLAN_LIMITS.plus : freeLimitsAt(now, changesFrom);
  /* A demo shows everything Liquidity+ does, and spends nothing on anyone's key. */
  if (user.isGuest) limits = { ...PLAN_LIMITS.plus, serverAi: false };
  else if (!enforced && !best) limits = PLAN_LIMITS.plus;

  return {
    plan: best || user.isGuest ? PLAN_PLUS : PLAN_FREE,
    source: user.isGuest ? 'demo' : (best?.source || null),
    until: user.isGuest ? null : (best?.until || null),
    enforced,
    launch: launch || null,
    limits,
    /* Said before the launch too, so the notice can name the date. */
    giftUntil: user.isGuest ? null : giftUntil
  };
}

/** How many are left of a limit; null when there is no limit. */
export function remaining(limit, used) {
  return limit === null || limit === undefined ? null : Math.max(0, limit - (used || 0));
}
